import { Avatar, Dropdown, Layout, Menu, Space, Tag, Typography } from 'antd';
import { LogoutOutlined, SwapOutlined, UserOutlined } from '@ant-design/icons';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import type { MenuProps } from 'antd';
import { useAuth } from '@/lib/useAuth';
import { availableShells, SHELL_LABEL, shellPath, type Shell } from '@/lib/shells';
import { NotificationBell } from '@/features/notifications/NotificationBell';
import { StatusMenu } from '@/features/presence/StatusMenu';
import { features } from '@/lib/features';
import { useFeature } from '@/lib/useFeature';

const { Header, Sider, Content } = Layout;

/**
 * The frame all three shells share.
 *
 * Extracted so Employee, Manager and Admin cannot drift apart in the small ways
 * that make a product feel like three products — different sign-out placement,
 * a header that moves, a sidebar that behaves differently.
 *
 * The shell switcher only appears for people who hold more than one shell's
 * roles. For the large majority — an employee with one role — there is nothing
 * to switch and no menu suggesting there might be.
 */
export function ShellChrome({
  shell,
  navItems,
  defaultOpenKeys,
  selfNavigatingPaths,
}: {
  shell: Shell;
  navItems: NonNullable<MenuProps["items"]>;
  defaultOpenKeys?: string[] | undefined;
  /**
   * Routes that are themselves a way of getting somewhere, and so do not need
   * the sidebar repeating it — the admin launcher is the only one today.
   *
   * Every other page keeps the sidebar, which is what stops this being a dead
   * end: a person lands on a module and Home is one click away, in the place it
   * has always been.
   */
  selfNavigatingPaths?: string[] | undefined;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { identity, logout } = useAuth();
  const collaboration = useFeature(features.collaboration);

  const roles = identity?.roles ?? [];
  const shells = availableShells(roles);
  const canSwitch = shells.length > 1;
  const showNav = !(selfNavigatingPaths ?? []).includes(location.pathname);

  const accountItems: MenuProps['items'] = [
    {
      key: 'roles',
      disabled: true,
      label: (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {roles.length > 0 ? roles.join(', ') : 'no roles'}
        </Typography.Text>
      ),
    },
    ...(canSwitch
      ? [
          { type: 'divider' as const },
          ...shells
            .filter((s) => s !== shell)
            .map((s) => ({
              key: s,
              icon: <SwapOutlined />,
              label: `Switch to ${SHELL_LABEL[s]}`,
              onClick: () => void navigate(shellPath(s)),
            })),
        ]
      : []),
    { type: 'divider' as const },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Sign out',
      onClick: () => void logout(),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          background: '#1e2b2b', // Eficaz dark slate, from the EfCodes logo
          paddingInline: 24,
        }}
      >
        {/* The product's wordmark as type: "Ef" in the logo's green. */}
        <Typography.Text strong style={{ color: '#fff', fontSize: 17, letterSpacing: 0.2, whiteSpace: 'nowrap' }}>
          <span style={{ color: '#2bb074' }}>Ef</span>People
        </Typography.Text>
        <Typography.Text style={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: 13 }}>
          {SHELL_LABEL[shell]}
        </Typography.Text>

        <div style={{ marginInlineStart: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          {collaboration && <StatusMenu />}
          <NotificationBell shell={shell} />
          <Dropdown menu={{ items: accountItems }}>
            <Space style={{ color: '#e2e8f0', cursor: 'pointer' }}>
              <Avatar size="small" icon={<UserOutlined />} />
              {/* Identifiers only in the chrome. The display name is personal
                  data and comes from the profile endpoint, which masks. */}
              <span style={{ fontSize: 13 }}>{roles[0] ?? 'Account'}</span>
              {canSwitch && (
                <Tag color="green" style={{ marginInlineStart: 4 }}>
                  {shells.length} views
                </Tag>
              )}
            </Space>
          </Dropdown>
        </div>
      </Header>

      <Layout>
        {showNav && (
          <Sider width={220} theme="light">
            <Menu
              mode="inline"
              selectedKeys={[location.pathname]}
              defaultOpenKeys={defaultOpenKeys ?? []}
              items={navItems}
              style={{ borderInlineEnd: 0, paddingTop: 8 }}
            />
          </Sider>
        )}

        <Content style={{ padding: 24, background: '#f8fafc' }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}

